import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logError } from "@faire/web--source/common/logging";
import findMessengerConversations from "@faire/web-api--source/endpoints/www/api/messenger/list-conversations/post";
import postStarConversation from "@faire/web-api--source/endpoints/www/api/messenger/star-unstar-conversation/post";
import fetchMessengerConversationByBrand from "@faire/web-api--source/endpoints/www/api/v3/messenger/brand-conversation/brandToken/get";
import fetchMessengerConversation from "@faire/web-api--source/endpoints/www/api/v3/messenger/conversation/conversationToken/get";
import fetchMessengerConversationByRetailer from "@faire/web-api--source/endpoints/www/api/v3/messenger/retailer-conversation/retailerToken/get";
import { trackMessengerMessageFromBrandToRetailer } from "@faire/web-api--source/events/messenger/actionUnknown/messageFromBrandToRetailer";
import { trackMessengerMessageFromRetailerToBrand } from "@faire/web-api--source/events/messenger/actionUnknown/messageFromRetailerToBrand";
import { IGetConversationResponseV3 } from "@faire/web-api--source/faire/messenger/IGetConversationResponseV3";
import { IGetMessengerConversationsRequest } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsRequest";
import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IMessengerConversation } from "@faire/web-api--source/indigofair/data/IMessengerConversation";
import { IMessengerMessage } from "@faire/web-api--source/indigofair/data/IMessengerMessage";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { IWebSocketMessage } from "@faire/web-api--source/indigofair/websockets/IWebSocketMessage";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

import { IMessageChannel, Message } from "../MessageChannel/MessageChannel";

import {
  ConversationModel,
  getConversationCreatedAt as getConversationModelCreatedAt,
  getConversationToken as getConversationModelToken,
  getConversationIdentifier as getConversationModelIdentifier,
  getConversationUserToken as getConversationModelUserToken,
  getConversationLatestMessage as getConversationModelLatestMessage,
  getConversationOtherPartyBrandToken as getConversationModelOtherPartyBrandToken,
  isConversationUnRead as isConversationModelUnRead,
  getConversationOtherPartyBusinessImage as getConversationModelOtherPartyBusinessImage,
  isConversationSupportConversation as isConversationModelSupportConversation,
  isConversationMarkedAsSpam as isConversationModelMarkedAsSpam,
  isConversationStarredByBrand as isConversationModelStarredByBrand,
  getConversationKey as getConversationModelKey,
  getConversationOrderedMessages as getConversationModelOrderedMessages,
  isConversationInputDisabled as isConversationModelInputDisabled,
  fetchAdditionalMessagesThrottled,
  getConversationOtherPartyUserType as getConversationModelOtherPartyUserType,
  getConversationParticipants as getConversationModelParticipants,
  getBrandCustomer,
  openConversation,
  getConversationLastModified as getConversationModelLastModified,
  isMessageFromCurrentUser as isMessageModelFromCurrentUser,
  isMessageFromOtherUser as isMessageModelFromOtherUser,
  isMessageFromSameRetailerAsCurrentUser as isMessageModelFromSameRetailerAsCurrentUser,
  isMessageFromSameBrandAsCurrentUser as isMessageModelFromSameBrandAsCurrentUser,
  isMessageRead as isMessageModelRead,
  isMessageLatestReadFromCurrentUser as isMessageModelLatestReadFromCurrentUser,
  isMessageLatestDelivered as isMessageModelLatestDelivered,
  sendMessage as sendMessageModel,
  deleteMessage as deleteMessageModel,
  getMessageOtherPartyProfilePicUrl as getMessageModelOtherPartyProfilePicUrl,
  getMessageCurrentPartyProfilePicUrl as getMessageModelCurrentPartyProfilePicUrl,
  getMessageOtherPartyFirstName as getMessageModelOtherPartyFirstName,
  getConversationMessageCount as getConversationModelMessageCount,
  setIsStarredByBrand,
  uploadImagesAndSendNewMessage,
  fetchMessages,
  createSyntheticStatusMessage,
  createConversationModel,
  updateConversation as updateConversationModel,
  firstFetchOrUpdateMessages,
} from "./ConversationModel";
import {
  MessageModel,
  shouldPopUp as getMessageModelShouldPopUp,
  getToken as getMessageModelToken,
} from "./MessageModel";
import { MessengerPollingFallback } from "./MessengerPollingFallback";
import { MessengerStore } from "./MessengerStore";
import { getBrandTokenInSession } from "./utils";

interface MessengerStoreContext {
  store?: MessengerStore;
}

const MessengerStoreReactContext = createContext<MessengerStoreContext>({});

export const MessengerStoreContextProvider: React.FC<
  React.PropsWithChildren<{ value: MessengerStoreContext }>
> = ({ children, value }) => {
  return (
    <MessengerStoreReactContext.Provider value={value}>
      <MessengerStoreEffectInitializers>
        {children}
      </MessengerStoreEffectInitializers>
    </MessengerStoreReactContext.Provider>
  );
};

const MessengerStoreEffectInitializersBase: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { query } = useMessengerStoreConversationList();
  const { lastSearchQuery, brandContext, retailerContext } =
    useMessengerStorePrivateFields();
  const { searchConversations } = useMessengerStoreConversationFetching();
  const searchConversationsDebounced = useMemo(
    () => debounce(searchConversations, 200),
    [searchConversations]
  );

  const contextToken = brandContext?.token ?? retailerContext?.token;
  useEffect(() => {
    if (query !== lastSearchQuery) {
      searchConversationsDebounced({
        query: query || undefined,
        context_token: contextToken,
      });
    }
  }, [query, lastSearchQuery, searchConversationsDebounced, contextToken]);

  return children;
};

const MessengerStoreEffectInitializers = observer(
  MessengerStoreEffectInitializersBase
);

export const useMessengerStore = () => {
  const { store } = useContext(MessengerStoreReactContext);
  if (!store) {
    // eslint-disable-next-line no-console
    console.warn("MessengerStore not initialized in useMessengerStore");
  }
  return store!;
};

export const useMessengerStoreOrUndefined = () => {
  const { store } = useContext(MessengerStoreReactContext);
  return store;
};

const getUseFields = <T extends (keyof MessengerStore)[]>(
  _: T
): (() => Pick<MessengerStore, T[number]>) => {
  return useMessengerStore;
};

// Conversation List Hooks
const useMessengerStoreConversationListFields = getUseFields([
  "showOnlyUnreadConversations",
  "showOnlyStarredByBrandConversations",
  "orderedConversationIdentifiers",
  "hasConversations",
  "isLoadingConversations",
  "unreadConversationCount",
  "setTotalUnreadConversations",
  "starredByBrandConversationCount",
  "setSearch",
  "query",
]);

export const useMessengerStoreConversationList = () => {
  const {
    orderedConversationIdentifiers,
    hasConversations,
    isLoadingConversations,
    unreadConversationCount,
    setTotalUnreadConversations,
    starredByBrandConversationCount,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
    setSearch,
    query,
  } = useMessengerStoreConversationListFields();

  const {
    nextConversations,
    hasConversationsNextRequest,
    firstFetchOrUpdateConversations,
  } = useMessengerStoreConversationFetching();

  const {
    setShowOnlyUnreadConversations:
      setShowOnlyUnreadConversationsInMessengerStore,
    setShowOnlyStarredByBrandConversations:
      setShowOnlyStarredByBrandConversationsInMessengerStore,
    unreadOnlyMainStreamFetchState,
    starredByBrandOnlyMainStreamFetchState,
  } = useMessengerStorePrivateFields();

  const { getConversationIsUnread, getConversationOtherPartyFirstName } =
    useMessengerStoreGetConversation();
  const latestUnreadConversationToken = orderedConversationIdentifiers.find(
    (conversationIdentifier) => getConversationIsUnread(conversationIdentifier)
  );

  const latestUnreadConversationOtherPartyFirstName =
    latestUnreadConversationToken
      ? getConversationOtherPartyFirstName(latestUnreadConversationToken)
      : undefined;
  const setShowOnlyUnreadConversations = useCallback(
    async (showOnlyUnreadConversations: boolean) => {
      setShowOnlyUnreadConversationsInMessengerStore(
        showOnlyUnreadConversations
      );
      if (showOnlyUnreadConversations) {
        unreadOnlyMainStreamFetchState.removeReadConversations();
      }
      return firstFetchOrUpdateConversations();
    },
    [
      unreadOnlyMainStreamFetchState,
      firstFetchOrUpdateConversations,
      setShowOnlyUnreadConversationsInMessengerStore,
    ]
  );

  const setShowOnlyStarredByBrandConversations = useCallback(
    async (showOnlyStarredByBrandConversations: boolean) => {
      setShowOnlyStarredByBrandConversationsInMessengerStore(
        showOnlyStarredByBrandConversations
      );
      if (showOnlyStarredByBrandConversations) {
        starredByBrandOnlyMainStreamFetchState.removeNonStarredByBrandConversations();
      }
      return firstFetchOrUpdateConversations();
    },
    [
      starredByBrandOnlyMainStreamFetchState,
      firstFetchOrUpdateConversations,
      setShowOnlyStarredByBrandConversationsInMessengerStore,
    ]
  );

  // Note: `...` spread syntax misses some fields here, not sure why, but we
  // list each field explicitly to prevent this.
  return {
    latestUnreadConversationOtherPartyFirstName,
    orderedConversationIdentifiers,
    hasConversations,
    isLoadingConversations,
    unreadConversationCount,
    setTotalUnreadConversations,
    starredByBrandConversationCount,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
    setShowOnlyUnreadConversations,
    setShowOnlyStarredByBrandConversations,
    setSearch,
    nextConversations,
    query,
    hasConversationsNextRequest,
  };
};

export const useMessengerStoreInit = () => {
  const store = useMessengerStoreOrUndefined();
  const { handleNewMessage } = useMessengerStoreConnectionManagement();
  const { firstFetchOrUpdateConversations } =
    useMessengerStoreConversationFetching();
  const init = useCallback(
    async (user: IUser, messageChannel: IMessageChannel | undefined) => {
      if (!store) {
        return;
      }
      const { setUser } = store;

      setUser(user);
      messageChannel?.subscribe(handleNewMessage);

      await firstFetchOrUpdateConversations();
    },
    [firstFetchOrUpdateConversations, handleNewMessage, store]
  );

  return useMemo(() => ({ init }), [init]);
};

export const useMessengerStoreConnectionManagement = () => {
  /**
   * These callbacks MUST support an undefined `store` at least when they
   * are defined, as they are called by `init` which is brought in to the
   * visitor package.
   */
  const store = useMessengerStoreOrUndefined();
  const {
    firstFetchOrUpdateConversations,
    createOrUpdateConversation,
    firstFetchOrUpdateMessagesInCurrentConversation,
  } = useMessengerStoreConversationFetching();

  const handleConnectionMessage = useCallback(
    (isConnected: boolean) => {
      if (!store) {
        return;
      }
      const {
        pollingFallback,
        setPollingFallback,
        wasConnected,
        setWasConnected,
      } = store;
      if (isConnected) {
        if (!wasConnected) {
          firstFetchOrUpdateConversations();
          firstFetchOrUpdateMessagesInCurrentConversation();
        }
        setWasConnected(true);
        if (pollingFallback) {
          pollingFallback.stop();
        }
      } else {
        if (!pollingFallback) {
          setPollingFallback(
            new MessengerPollingFallback(() => {
              firstFetchOrUpdateConversations();
              firstFetchOrUpdateMessagesInCurrentConversation();
            }, wasConnected)
          );
        }
      }
    },
    [
      store,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
    ]
  );

  const handleNewMessage = useCallback(
    (message: Message) => {
      if (
        message.type === IWebSocketMessage.Type.CONVERSATION_UPDATED &&
        message.updated_conversation
      ) {
        createOrUpdateConversation(message.updated_conversation);
      }

      if (message.type === "CONNECTION") {
        handleConnectionMessage(message.isConnected);
      }
    },
    [createOrUpdateConversation, handleConnectionMessage]
  );

  return useMemo(
    () => ({
      handleConnectionMessage,
      handleNewMessage,
    }),
    [handleConnectionMessage, handleNewMessage]
  );
};

// Conversation Management Hooks
export const useMessengerStoreConversationManagement = () => {
  const { starredByBrandConversationCount } =
    useMessengerStoreConversationListFields();
  const {
    getConversationByIdentifier,
    setTotalStarredByBrandConversations,
    hiddenConversations,
    storeUpdatedConversation,
  } = useMessengerStorePrivateFields();
  const { firstFetchOrUpdateConversations } =
    useMessengerStoreConversationFetching();

  const setConversationIsStarredByBrand = useCallback(
    async (conversationToken: string, isStarred: boolean) => {
      // Update the number of starred conversations immediately
      if (isStarred) {
        setTotalStarredByBrandConversations(
          starredByBrandConversationCount + 1
        );
      } else {
        setTotalStarredByBrandConversations(
          Math.max(0, starredByBrandConversationCount - 1)
        );
      }

      // Immediately updates star icon locally
      const conversation = getConversationByIdentifier(conversationToken);
      if (conversation) {
        const updatedConversation = setIsStarredByBrand(
          conversation,
          isStarred
        );
        storeUpdatedConversation(updatedConversation);
      }

      // Submit the endpoint to actually update the conversation, and
      // re-fetch the conversations, to align with backend state.
      await postStarConversation({
        conversation_token: conversationToken,
        star: isStarred,
      });
      await firstFetchOrUpdateConversations();
    },
    [
      getConversationByIdentifier,
      firstFetchOrUpdateConversations,
      setTotalStarredByBrandConversations,
      starredByBrandConversationCount,
      storeUpdatedConversation,
    ]
  );

  const toggleConversationIsStarredByBrand = useCallback(
    async (conversationToken: string) => {
      const conversation = getConversationByIdentifier(conversationToken);
      if (!conversation) {
        throw new Error(`conversation not found: ${conversationToken}`);
      }

      await setConversationIsStarredByBrand(
        conversationToken,
        !isConversationModelStarredByBrand(conversation)
      );
    },
    [getConversationByIdentifier, setConversationIsStarredByBrand]
  );

  const hideConversation = useCallback(
    (conversationToken: string) => {
      if (hiddenConversations) {
        hiddenConversations.push(conversationToken);
      }
    },
    [hiddenConversations]
  );

  const fetchConversationMessages = useCallback(
    async (conversationToken: string) => {
      const conversation = getConversationByIdentifier(conversationToken);
      if (conversation) {
        const updatedConversation = await fetchMessages(conversation);
        storeUpdatedConversation(updatedConversation);
      }
    },
    [getConversationByIdentifier, storeUpdatedConversation]
  );

  const sendMessageInConversation = useCallback(
    async (
      conversationIdentifier: string,
      message: Partial<IMessengerMessage>,
      images: readonly File[] | undefined,
      products: string[] = []
    ): Promise<void> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        throw new Error("No conversation found for " + conversationIdentifier);
      }

      try {
        const messageData = {
          text: message.text ?? "",
          images: message.images ?? [],
        };

        const updatedConversation = await uploadImagesAndSendNewMessage(
          conversation,
          messageData,
          images,
          products
        );
        storeUpdatedConversation(updatedConversation);
      } catch (e) {
        throw new Error("Message sending failed: " + e);
      }
    },
    [getConversationByIdentifier, storeUpdatedConversation]
  );

  return useMemo(
    () => ({
      setConversationIsStarredByBrand,
      toggleConversationIsStarredByBrand,
      hideConversation,
      fetchConversationMessages,
      sendMessageInConversation,
      firstFetchOrUpdateConversations,
    }),
    [
      setConversationIsStarredByBrand,
      toggleConversationIsStarredByBrand,
      hideConversation,
      fetchConversationMessages,
      sendMessageInConversation,
      firstFetchOrUpdateConversations,
    ]
  );
};

// Context Management Hooks
export const useMessengerStoreUser = () => {
  const { user } = useMessengerStorePrivateFields();
  const isBrandUser = user?.type === IUser.Type.BRAND_USER;
  return useMemo(() => ({ isBrandUser }), [isBrandUser]);
};

export const useMessengerStoreBrandContext = () => {
  /**
   * Raise an error if the `MessengerStore` is not defined, and otherwise share
   * implementations with the version of this hook that permits the store to be
   * undefined.
   */
  void useMessengerStore();
  return useMessengerStoreBrandContextOrUndefined();
};

export const useMessengerStoreRetailerContext = () => {
  const {
    retailerContext,
    setQuery,
    conversationByRetailerToken,
    conversationByUserToken,
  } = useMessengerStorePrivateFields();
  const { fetchConversations } = useMessengerStoreConversationFetching();

  const updateCurrentRetailerContext = useCallback(
    async (retailer?: IRetailer) => {
      if (retailer?.token === retailerContext?.token) {
        return;
      }

      setQuery("");

      // If we have a brand context that we're switching from, see if the
      // conversation has no messages. If so, remove it.
      // Don't check conversationByConversationToken because a conversation
      // has to have messages to have a conversation_token
      if (retailerContext?.token && retailerContext.owner_user_token) {
        const conversation = conversationByRetailerToken.get(
          retailerContext.token
        );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          conversationByUserToken.delete(retailerContext.owner_user_token);
          conversationByRetailerToken.delete(retailerContext.token);
        }
      }

      if (retailer) {
        await fetchConversations({
          context_token: retailer.token,
        });
      }
    },
    [
      retailerContext,
      setQuery,
      conversationByRetailerToken,
      conversationByUserToken,
      fetchConversations,
    ]
  );

  return useMemo(
    () => ({
      currentRetailerContext: retailerContext,
      updateCurrentRetailerContext,
    }),
    [retailerContext, updateCurrentRetailerContext]
  );
};

export const isConversationToken = (token: string): boolean => {
  return token.startsWith("mc_");
};

// Individual Conversation Hooks
export const useMessengerStoreGetConversation = () => {
  const { strictLocalize } = useStrictLocalization();
  const {
    getConversationByIdentifier,
    conversationByBrandToken,
    conversationByRetailerToken,
    storeUpdatedConversation,
    setTotalUnreadConversations,
  } = useMessengerStorePrivateFields();

  // This `useCallback` is here to prevent the function literal from being
  // over-reactive. Technically it's under-reactive, here, since
  // `conversationByConversationToken` is mutated and not re-created, but
  // because we capture it by-reference and the callback operates on it
  // only at call-time, it's actually fine.
  const hasConversation = useCallback(
    (conversationIdentifier: string) => {
      return !!getConversationByIdentifier(conversationIdentifier);
    },
    [getConversationByIdentifier]
  );

  const getConversationToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? getConversationModelToken(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationCreatedAt = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelCreatedAt(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationLatestMessage = useCallback(
    (conversationIdentifier: string): MessageModel | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelLatestMessage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationRetailer = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.retailer;
    },
    [getConversationByIdentifier]
  );

  const getConversationBrand = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.brand;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyType = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.type;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyUserToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyRetailerToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.retailer_token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBrandToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOtherPartyBrandToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsUnread = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? isConversationModelUnRead(conversation) : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyFirstName = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.first_name;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBusinessImage = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOtherPartyBusinessImage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsSupportConversation = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelSupportConversation(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsMarkedAsSpam = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelMarkedAsSpam(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsStarredByBrand = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelStarredByBrand(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationUserToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelUserToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyCompanyName = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }

      let companyName = "";
      if (conversation.otherParty?.type === IUser.Type.ADMIN) {
        companyName = strictLocalize({
          defaultMessage: "Faire Support",
          description: {
            text: "Name of the other party when chatting with Faire Support",
          },
        });
      } else if (
        conversation.otherParty?.type === IUser.Type.USER &&
        conversation.retailer &&
        conversation.retailer.name
      ) {
        companyName = conversation.retailer.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.BRAND_USER &&
        conversation.brand &&
        conversation.brand.name
      ) {
        companyName = conversation.brand.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.GUEST &&
        conversation.otherPartyCompany
      ) {
        companyName = conversation.otherPartyCompany;
      }
      return companyName;
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getConversationIdentifier = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }

      return (
        getConversationModelToken(conversation) ??
        conversation.brand?.token ??
        conversation.retailer?.token ??
        getConversationModelUserToken(conversation)
      );
    },
    [getConversationByIdentifier]
  );

  const getConversationKey = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? getConversationModelKey(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherParty = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyProfileImage = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.profile_image;
    },
    [getConversationByIdentifier]
  );

  const getConversationOrderedMessages = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOrderedMessages(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsInputDisabled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelInputDisabled(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsOtherPartyBrandCustomer = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)
        ?.isOtherPartyBrandCustomer;
    },
    [getConversationByIdentifier]
  );

  const getConversationFetchAdditionalMessagesThrottled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      const fetchAdditionalMessagesThrottledCallback =
        fetchAdditionalMessagesThrottled(conversation);
      return async () => {
        const updatedConversation =
          await fetchAdditionalMessagesThrottledCallback();
        storeUpdatedConversation(updatedConversation);
        return updatedConversation;
      };
    },
    [getConversationByIdentifier, storeUpdatedConversation]
  );

  const getConversationOtherPartyUserType = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOtherPartyUserType(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationParticipants = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelParticipants(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationShouldShowLoadingSpinner = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation?.isLoading || !!conversation?.nextRequest;
    },
    [getConversationByIdentifier]
  );

  const getConversationFetchBrandCustomer = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? () => getBrandCustomer(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOpenConversation = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? async () => {
            const [updatedConversation, totalUnreadConversations] =
              await openConversation(conversation);
            storeUpdatedConversation(updatedConversation);
            setTotalUnreadConversations(totalUnreadConversations);
            return updatedConversation;
          }
        : undefined;
    },
    [
      getConversationByIdentifier,
      setTotalUnreadConversations,
      storeUpdatedConversation,
    ]
  );

  const getConversationMessageAuthors = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)
        ?.messageAuthors;
    },
    [getConversationByIdentifier]
  );

  const hasConversationWithRetailer = useCallback(
    (retailerToken: string) => {
      return !!conversationByRetailerToken.get(retailerToken);
    },
    [conversationByRetailerToken]
  );

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return useMemo(
    () => ({
      hasConversation,
      getConversationToken,
      getConversationCreatedAt,
      getConversationLatestMessage,
      getConversationRetailer,
      getConversationBrand,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyBrandToken,
      getConversationIsUnread,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyBusinessImage,
      getConversationIsSupportConversation,
      getConversationIsMarkedAsSpam,
      getConversationIsStarredByBrand,
      getConversationOtherPartyCompanyName,
      getConversationIdentifier,
      getConversationKey,
      getConversationOtherParty,
      getConversationUserToken,
      getConversationOtherPartyProfileImage,
      getConversationOrderedMessages,
      getConversationIsInputDisabled,
      getConversationIsOtherPartyBrandCustomer,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationShouldShowLoadingSpinner,
      getConversationFetchBrandCustomer,
      getConversationOpenConversation,
      getConversationMessageAuthors,
      isConversationToken,
      hasConversationWithRetailer,
      hasConversationWithBrand,
    }),
    [
      getConversationToken,
      getConversationBrand,
      getConversationCreatedAt,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationFetchBrandCustomer,
      getConversationIdentifier,
      getConversationIsInputDisabled,
      getConversationIsMarkedAsSpam,
      getConversationIsOtherPartyBrandCustomer,
      getConversationIsStarredByBrand,
      getConversationIsSupportConversation,
      getConversationIsUnread,
      getConversationKey,
      getConversationLatestMessage,
      getConversationMessageAuthors,
      getConversationOpenConversation,
      getConversationOrderedMessages,
      getConversationOtherParty,
      getConversationOtherPartyBrandToken,
      getConversationOtherPartyBusinessImage,
      getConversationOtherPartyCompanyName,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyProfileImage,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationRetailer,
      getConversationShouldShowLoadingSpinner,
      getConversationUserToken,
      hasConversation,
      hasConversationWithBrand,
      hasConversationWithRetailer,
    ]
  );
};

export const useMessengerStoreLatestUnreadPopUpConversation = () => {
  // Note: this cannot currently be memoized because `conversationByUserToken`
  // is mutated.
  const { conversationByUserToken } = useMessengerStorePrivateFields();

  // Use traditional for loop to avoid property access issues
  const conversations = Array.from(conversationByUserToken?.values() ?? []);
  let latestUnreadPopUpConversation = null;

  for (const conversation of conversations) {
    const latestMessage = getConversationModelLatestMessage(conversation);
    const isFromOtherUser =
      latestMessage && isMessageModelFromOtherUser(latestMessage, conversation);
    if (
      latestMessage &&
      isFromOtherUser &&
      getMessageModelShouldPopUp(latestMessage) &&
      !isMessageModelRead(latestMessage, conversation)
    ) {
      if (!latestUnreadPopUpConversation) {
        latestUnreadPopUpConversation = conversation;
      } else {
        const currentModified =
          getConversationModelLastModified(conversation) || 0;
        const latestModified =
          getConversationModelLastModified(latestUnreadPopUpConversation) || 0;
        if (currentModified > latestModified) {
          latestUnreadPopUpConversation = conversation;
        }
      }
    }
  }

  const latestUnreadPopUpConversationConversationToken =
    latestUnreadPopUpConversation
      ? getConversationModelToken(latestUnreadPopUpConversation)
      : undefined;

  const {
    getConversationOtherPartyUserToken,
    getConversationOtherPartyBrandToken,
    getConversationOtherPartyRetailerToken,
  } = useMessengerStoreGetConversation();

  const latestUnreadPopUpConversationOtherPartyUserToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyUserToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;
  const latestUnreadPopUpConversationOtherPartyBrandToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyBrandToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;
  const latestUnreadPopUpConversationOtherPartyRetailerToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyRetailerToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;

  const hasUnreadPopUpConversation =
    !!latestUnreadPopUpConversationConversationToken;

  return useMemo(
    () => ({
      latestUnreadPopUpConversationConversationToken,
      latestUnreadPopUpConversationOtherPartyUserToken,
      latestUnreadPopUpConversationOtherPartyBrandToken,
      latestUnreadPopUpConversationOtherPartyRetailerToken,
      hasUnreadPopUpConversation,
    }),
    [
      latestUnreadPopUpConversationConversationToken,
      latestUnreadPopUpConversationOtherPartyUserToken,
      latestUnreadPopUpConversationOtherPartyBrandToken,
      latestUnreadPopUpConversationOtherPartyRetailerToken,
      hasUnreadPopUpConversation,
    ]
  );
};

// eslint-disable-next-line complexity
export const useMessengerStoreCurrentConversation = () => {
  const {
    currentConversationIdentifier,
    setCurrentConversationIdentifier,
    hasConversationWithIdentifier,
  } = useMessengerStorePrivateFields();

  const {
    getConversationKey,
    getConversationToken,
    getConversationOtherPartyUserToken,
    getConversationOtherPartyBrandToken,
    getConversationOtherPartyRetailerToken,
    getConversationOtherParty,
    getConversationUserToken,
    getConversationBrand,
    getConversationRetailer,
    getConversationOtherPartyProfileImage,
    getConversationOtherPartyCompanyName,
    getConversationOtherPartyBusinessImage,
    getConversationLatestMessage,
    getConversationOrderedMessages,
    getConversationIsUnread,
    getConversationIsInputDisabled,
    getConversationIsMarkedAsSpam,
    getConversationIsOtherPartyBrandCustomer,
    getConversationIsStarredByBrand,
    getConversationFetchAdditionalMessagesThrottled,
    getConversationOtherPartyUserType,
    getConversationParticipants,
    getConversationOtherPartyFirstName,
    getConversationShouldShowLoadingSpinner,
    getConversationFetchBrandCustomer,
    getConversationOpenConversation,
    getConversationMessageAuthors,
  } = useMessengerStoreGetConversation();

  const { sendMessageInConversation } =
    useMessengerStoreConversationManagement();

  const { setCurrentConversation } = useMessengerStoreConversationFetching();

  const hasCurrentConversation = !!currentConversationIdentifier;

  // Added helper to get the current conversation from the store
  const { getConversationByIdentifier } = useMessengerStorePrivateFields();
  const getCurrentConversation = useCallback(() => {
    if (!currentConversationIdentifier) {
      return undefined;
    }
    return getConversationByIdentifier(currentConversationIdentifier);
  }, [currentConversationIdentifier, getConversationByIdentifier]);

  const currentConversationToken = currentConversationIdentifier
    ? getConversationToken(currentConversationIdentifier)
    : undefined;
  const currentConversationKey = currentConversationIdentifier
    ? getConversationKey(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyUserToken = currentConversationIdentifier
    ? getConversationOtherPartyUserToken(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyBrandToken = currentConversationIdentifier
    ? getConversationOtherPartyBrandToken(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyRetailerToken =
    currentConversationIdentifier
      ? getConversationOtherPartyRetailerToken(currentConversationIdentifier)
      : undefined;
  const currentConversationOtherParty = currentConversationIdentifier
    ? getConversationOtherParty(currentConversationIdentifier)
    : undefined;
  const currentConversationUserToken = currentConversationIdentifier
    ? getConversationUserToken(currentConversationIdentifier)
    : undefined;
  const currentConversationBrand = currentConversationIdentifier
    ? getConversationBrand(currentConversationIdentifier)
    : undefined;
  const currentConversationRetailer = currentConversationIdentifier
    ? getConversationRetailer(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyProfileImage =
    currentConversationIdentifier
      ? getConversationOtherPartyProfileImage(currentConversationIdentifier)
      : undefined;
  const currentConversationOtherPartyCompanyName = currentConversationIdentifier
    ? getConversationOtherPartyCompanyName(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyBusinessImage =
    currentConversationIdentifier
      ? getConversationOtherPartyBusinessImage(currentConversationIdentifier)
      : undefined;
  const currentConversationLatestMessage = currentConversationIdentifier
    ? getConversationLatestMessage(currentConversationIdentifier)
    : undefined;
  const currentConversationOrderedMessages = currentConversationIdentifier
    ? getConversationOrderedMessages(currentConversationIdentifier)
    : undefined;
  const currentConversationIsUnread = currentConversationIdentifier
    ? getConversationIsUnread(currentConversationIdentifier)
    : undefined;
  const currentConversationIsInputDisabled = currentConversationIdentifier
    ? getConversationIsInputDisabled(currentConversationIdentifier)
    : undefined;
  const currentConversationIsMarkedAsSpam = currentConversationIdentifier
    ? getConversationIsMarkedAsSpam(currentConversationIdentifier)
    : undefined;
  const currentConversationIsOtherPartyBrandCustomer =
    currentConversationIdentifier
      ? getConversationIsOtherPartyBrandCustomer(currentConversationIdentifier)
      : undefined;
  const currentConversationIsStarredByBrand = currentConversationIdentifier
    ? getConversationIsStarredByBrand(currentConversationIdentifier)
    : undefined;

  // We want the type of `currentConversationFetchAdditionalMessagesThrottled` to be
  // a function instead of function | undefined so that we don't change the hooks
  // interface and have to refactor consumers. Technically this `useMemo` is
  // under-reactive; `getConversationFetchAdditionalMessagesThrottled` doesn't
  // get recomputed when the conversation mapping changes. However, because it closes
  // over `this`, it accesses the conversation mapping by-reference, so it's
  // actually okay.
  const currentConversationFetchAdditionalMessagesThrottled =
    useCallback(() => {
      if (currentConversationIdentifier) {
        getConversationFetchAdditionalMessagesThrottled(
          currentConversationIdentifier
        )?.();
      }
    }, [
      currentConversationIdentifier,
      getConversationFetchAdditionalMessagesThrottled,
    ]);

  const currentConversationOtherPartyUserType = currentConversationIdentifier
    ? getConversationOtherPartyUserType(currentConversationIdentifier)
    : undefined;
  const currentConversationParticipants = currentConversationIdentifier
    ? getConversationParticipants(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyFirstName = currentConversationIdentifier
    ? getConversationOtherPartyFirstName(currentConversationIdentifier)
    : undefined;
  const currentConversationShouldShowLoadingSpinner =
    currentConversationIdentifier
      ? getConversationShouldShowLoadingSpinner(currentConversationIdentifier)
      : undefined;

  const currentConversationFetchBrandCustomer = useCallback(() => {
    if (currentConversationIdentifier) {
      getConversationFetchBrandCustomer(currentConversationIdentifier)?.();
    }
  }, [currentConversationIdentifier, getConversationFetchBrandCustomer]);

  const currentConversationOpenConversation = useCallback(() => {
    if (currentConversationIdentifier) {
      getConversationOpenConversation(currentConversationIdentifier)?.();
    }
  }, [currentConversationIdentifier, getConversationOpenConversation]);

  const currentConversationMessageAuthors = currentConversationIdentifier
    ? getConversationMessageAuthors(currentConversationIdentifier)
    : undefined;

  const sendMessageInCurrentConversation = useCallback(
    async (
      message: Partial<IMessengerMessage>,
      images: readonly File[] | undefined,
      products: string[] = []
    ) => {
      if (currentConversationIdentifier) {
        await sendMessageInConversation(
          currentConversationIdentifier,
          message,
          images,
          products
        );
      }
      return Promise.resolve();
    },
    [currentConversationIdentifier, sendMessageInConversation]
  );

  const updateCurrentConversationFromIdentifier = useCallback(
    (conversationIdentifier: string | undefined) => {
      if (!conversationIdentifier) {
        setCurrentConversationIdentifier(undefined);
        return;
      }

      if (
        !hasConversationWithIdentifier(conversationIdentifier) &&
        !hasCurrentConversation
      ) {
        return;
      }

      const conversationTokenMatch =
        getConversationToken(conversationIdentifier) ===
        (currentConversationIdentifier
          ? getConversationToken(currentConversationIdentifier)
          : undefined);
      const brandTokenMatch =
        getConversationOtherPartyBrandToken(conversationIdentifier) ===
        (currentConversationIdentifier
          ? getConversationOtherPartyBrandToken(currentConversationIdentifier)
          : undefined);
      const retailerTokenMatch =
        getConversationOtherParty(conversationIdentifier)?.retailer_token ===
        (currentConversationIdentifier
          ? getConversationOtherParty(currentConversationIdentifier)
              ?.retailer_token
          : undefined);

      setCurrentConversationIdentifier(conversationIdentifier);

      if (
        !conversationTokenMatch &&
        (!brandTokenMatch || !retailerTokenMatch)
      ) {
        setCurrentConversation({
          otherPartyUserToken: getConversationUserToken(conversationIdentifier),
          retailerToken: getConversationOtherParty(conversationIdentifier)
            ?.retailer_token,
          brandToken: getConversationOtherPartyBrandToken(
            conversationIdentifier
          ),
          conversationToken: getConversationToken(conversationIdentifier),
        });
      }
    },
    [
      hasConversationWithIdentifier,
      hasCurrentConversation,
      getConversationToken,
      currentConversationIdentifier,
      getConversationOtherPartyBrandToken,
      getConversationOtherParty,
      setCurrentConversationIdentifier,
      setCurrentConversation,
      getConversationUserToken,
    ]
  );

  return useMemo(
    () => ({
      currentConversationIdentifier,
      currentConversationKey,
      currentConversationToken,
      currentConversationOtherPartyUserToken,
      currentConversationOtherPartyBrandToken,
      currentConversationOtherPartyRetailerToken,
      currentConversationOtherParty,
      currentConversationUserToken,
      currentConversationBrand,
      currentConversationRetailer,
      currentConversationOtherPartyProfileImage,
      currentConversationOtherPartyCompanyName,
      currentConversationOtherPartyBusinessImage,
      currentConversationLatestMessage,
      currentConversationOrderedMessages,
      currentConversationIsUnread,
      currentConversationIsInputDisabled,
      currentConversationIsMarkedAsSpam,
      currentConversationIsOtherPartyBrandCustomer,
      currentConversationIsStarredByBrand,
      currentConversationFetchAdditionalMessagesThrottled,
      currentConversationOtherPartyUserType,
      currentConversationParticipants,
      currentConversationOtherPartyFirstName,
      currentConversationShouldShowLoadingSpinner,
      currentConversationFetchBrandCustomer,
      currentConversationOpenConversation,
      currentConversationMessageAuthors,
      hasCurrentConversation,
      setCurrentConversation,
      updateCurrentConversationFromIdentifier,
      sendMessageInCurrentConversation,
      getCurrentConversation,
    }),
    [
      currentConversationBrand,
      currentConversationFetchAdditionalMessagesThrottled,
      currentConversationFetchBrandCustomer,
      currentConversationIdentifier,
      currentConversationIsInputDisabled,
      currentConversationIsMarkedAsSpam,
      currentConversationIsOtherPartyBrandCustomer,
      currentConversationIsStarredByBrand,
      currentConversationIsUnread,
      currentConversationKey,
      currentConversationLatestMessage,
      currentConversationMessageAuthors,
      currentConversationOpenConversation,
      currentConversationOrderedMessages,
      currentConversationOtherParty,
      currentConversationOtherPartyBrandToken,
      currentConversationOtherPartyBusinessImage,
      currentConversationOtherPartyCompanyName,
      currentConversationOtherPartyFirstName,
      currentConversationOtherPartyProfileImage,
      currentConversationOtherPartyRetailerToken,
      currentConversationOtherPartyUserToken,
      currentConversationOtherPartyUserType,
      currentConversationParticipants,
      currentConversationRetailer,
      currentConversationShouldShowLoadingSpinner,
      currentConversationToken,
      currentConversationUserToken,
      hasCurrentConversation,
      sendMessageInCurrentConversation,
      setCurrentConversation,
      updateCurrentConversationFromIdentifier,
      getCurrentConversation,
    ]
  );
};

export const useMessengerStorePrivateFields = getUseFields([
  "user",
  "setUser",
  "brand",
  "getConversationByIdentifier",
  "conversationByUserToken",
  "conversationByConversationToken",
  "conversationByRetailerToken",
  "conversationByBrandToken",
  "currentConversationIdentifier",
  "setTotalUnreadConversations",
  "setTotalStarredByBrandConversations",
  "hiddenConversations",
  "storeUpdatedConversation",
  "brandContext",
  "retailerContext",
  "setShowOnlyUnreadConversations",
  "setShowOnlyStarredByBrandConversations",
  "setQuery",
  "setBrandContext",
  "pollingFallback",
  "setPollingFallback",
  "wasConnected",
  "setWasConnected",
  "unreadOnlyMainStreamFetchState",
  "starredByBrandOnlyMainStreamFetchState",
  "mainStreamFetchState",
  "searchResults",
  "setSearchResults",
  "lastSearchQuery",
  "hasConversationWithIdentifier",
  "incrementIsLoadingCount",
  "decrementIsLoadingCount",
  "latestMessageUpdatedAt",
  "setLatestMessageUpdatedAt",
  "query",
  "showOnlyUnreadConversations",
  "showOnlyStarredByBrandConversations",
  "setCurrentConversationIdentifier",
  "searchForConversationInFetchStreams",
  "setTotalUnreadConversations",
]);

export const useMessengerStoreGetConversationOrUndefined = () => {
  const store = useMessengerStoreOrUndefined();
  const { conversationByBrandToken } = store ?? {};

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken?.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return useMemo(
    () => ({ hasConversationWithBrand }),
    [hasConversationWithBrand]
  );
};

export const useMessengerStoreBrandContextOrUndefined = () => {
  const store = useMessengerStoreOrUndefined();
  const { fetchConversations } = useMessengerStoreConversationFetching();

  /**
   * Sets the conversation as the first conversation in the list.
   *
   * Useful when you're on a page that refers to this conversation
   * ie. brand page, contact, brand order
   */
  const setCurrentBrandContext = useCallback(
    async (brand?: IGetMessengerConversationsResponse.ISimpleBrand) => {
      if (!store) {
        return;
      }
      const {
        conversationByBrandToken,
        conversationByUserToken,
        brandContext,
        setQuery,
        setBrandContext,
      } = store;

      if (brand?.token === brandContext?.token) {
        return;
      }

      setQuery("");

      // If we have a brand context that we're switching from, see if the
      // conversation has no messages. If so, remove it.
      // Don't check conversationByConversationToken because a conversation
      // has to have messages to have a conversation_token
      if (brandContext) {
        const conversation = conversationByBrandToken.get(brandContext!.token!);
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          conversationByUserToken.delete(brandContext!.owner_user_token!);
          conversationByBrandToken.delete(brandContext!.token!);
        }
      }

      let brandContextToUse:
        | IGetMessengerConversationsResponse.ISimpleBrand
        | undefined = brand;
      if (brand && !brand.hide_chat) {
        setBrandContext(brand);
        brandContextToUse = brand;
      } else {
        setBrandContext(undefined);
        brandContextToUse = undefined;
      }

      if (brandContextToUse) {
        await fetchConversations({
          context_token: brandContextToUse.token,
        });
      }
    },
    [fetchConversations, store]
  );

  return useMemo(() => ({ setCurrentBrandContext }), [setCurrentBrandContext]);
};

export const useMessengerStoreConversationMessage = () => {
  const { strictLocalize } = useStrictLocalization();
  const { brand, getConversationByIdentifier } =
    useMessengerStorePrivateFields();

  const isMessageFromCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromCurrentUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageFromOtherUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromOtherUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageFromSameRetailerAsCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromSameRetailerAsCurrentUser(
        message,
        conversation,
        conversation.messageAuthors
      );
    },
    [getConversationByIdentifier]
  );

  const isMessageFromSameBrandAsCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromSameBrandAsCurrentUser(
        message,
        conversation,
        conversation.messageAuthors
      );
    },
    [getConversationByIdentifier]
  );

  const isMessageRead = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelRead(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageLatestReadFromCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelLatestReadFromCurrentUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageLatestDelivered = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelLatestDelivered(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const sendMessage = useCallback(
    (message: MessageModel, conversationIdentifier: string): Promise<void> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return Promise.resolve();
      }
      return sendMessageModel(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const deleteMessage = useCallback(
    (message: MessageModel, conversationIdentifier: string): Promise<void> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return Promise.resolve();
      }
      return (
        deleteMessageModel(
          conversation,
          getMessageModelToken(message),
          strictLocalize
        )
          // Cast to `Promise<void>` to satisfy the type checker
          .then(() => {})
      );
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getOtherPartyProfilePicUrl = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelOtherPartyProfilePicUrl(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const getCurrentPartyProfilePicUrl = useCallback(
    (
      _message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelCurrentPartyProfilePicUrl(conversation, brand);
    },
    [brand, getConversationByIdentifier]
  );

  const getMessageOtherPartyFirstName = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelOtherPartyFirstName(message, conversation);
    },
    [getConversationByIdentifier]
  );

  return useMemo(
    () => ({
      isMessageFromCurrentUser,
      isMessageFromOtherUser,
      isMessageFromSameRetailerAsCurrentUser,
      isMessageFromSameBrandAsCurrentUser,
      isMessageRead,
      isMessageLatestReadFromCurrentUser,
      isMessageLatestDelivered,
      sendMessage,
      deleteMessage,
      getOtherPartyProfilePicUrl,
      getCurrentPartyProfilePicUrl,
      getMessageOtherPartyFirstName,
    }),
    [
      isMessageFromCurrentUser,
      isMessageFromOtherUser,
      isMessageFromSameRetailerAsCurrentUser,
      isMessageFromSameBrandAsCurrentUser,
      isMessageRead,
      isMessageLatestReadFromCurrentUser,
      isMessageLatestDelivered,
      sendMessage,
      deleteMessage,
      getOtherPartyProfilePicUrl,
      getCurrentPartyProfilePicUrl,
      getMessageOtherPartyFirstName,
    ]
  );
};

/**
 * Hooks used internally for fetching conversations. Note these are referenced by
 * hooks that are brought in on the visitor pages so *all* hooks here must at least
 * support being *defined* in the absence of a MessengerStore.
 */
const useMessengerStoreConversationFetching = () => {
  const { strictLocalize } = useStrictLocalization();
  const store = useMessengerStoreOrUndefined();

  const createOrUpdateConversation = useCallback(
    // eslint-disable-next-line complexity
    (
      conversation: IMessengerConversation,
      simple_brand?: IGetMessengerConversationsResponse.ISimpleBrand,
      simple_retailer?: IGetMessengerConversationsResponse.ISimpleRetailer
    ) => {
      if (!store) {
        return;
      }

      const {
        user,
        searchForConversationInFetchStreams,
        storeUpdatedConversation,
        latestMessageUpdatedAt,
        setLatestMessageUpdatedAt,
      } = store;

      const otherUser = conversation.other_party;
      const otherIsBrandUser =
        otherUser && otherUser.type === IUser.Type.BRAND_USER;
      const otherIsRetailerUser =
        otherUser && otherUser.type === IUser.Type.USER;
      const otherPartyUserToken =
        conversation.other_party && conversation.other_party.token;

      // Multi-brand allows users to be linked to multiple brands and switch between them.
      // As a result, a WebSocket channel may have been initiated and connected by a different brand.
      // Therefore, when receiving a message from the WebSocket, we need to verify if the message is intended for the current brand.
      // More details in https://www.notion.so/faire/Test-Spec-Multi-Brand-1272efb5c25a80bd853dcc6bf66f9791?pvs=4#1512efb5c25a80299108eb4fd5453f5b
      const currentBrandToken = getBrandTokenInSession();
      const isMessageForAnotherBrand =
        otherIsRetailerUser && conversation.brand_token !== currentBrandToken;

      if (!otherUser || !otherPartyUserToken || isMessageForAnotherBrand) {
        return;
      }

      let updatedConversation =
        searchForConversationInFetchStreams(conversation);

      if (updatedConversation) {
        updatedConversation = updateConversationModel(
          updatedConversation,
          conversation,
          simple_brand,
          simple_retailer
        );
        updatedConversation.user = user ?? IUser.build({});
      } else {
        updatedConversation = createConversationModel(
          conversation,
          strictLocalize,
          user,
          simple_brand,
          simple_retailer
        );
      }

      updatedConversation = createSyntheticStatusMessage(
        updatedConversation,
        updatedConversation.data?.other_party_status
      );

      storeUpdatedConversation(updatedConversation);

      if (
        conversation.latest_message?.author_token === otherUser.token &&
        otherIsBrandUser &&
        latestMessageUpdatedAt !== conversation.latest_message?.updated_at &&
        conversation.latest_message?.text !== "review request"
      ) {
        trackMessengerMessageFromBrandToRetailer(
          conversation?.retailer_token ?? "",
          conversation.brand_token ?? "",
          conversation.latest_message?.updated_at?.toString() ?? "",
          conversation.latest_message?.text ?? ""
        );
        setLatestMessageUpdatedAt(conversation.latest_message?.updated_at);
      }

      if (
        conversation.latest_message?.author_token === otherUser.token &&
        otherIsRetailerUser &&
        latestMessageUpdatedAt !== conversation.latest_message?.updated_at &&
        conversation.latest_message?.text !== "review request"
      ) {
        trackMessengerMessageFromRetailerToBrand(
          "",
          conversation.brand_token ?? "",
          conversation?.retailer_token ?? "",
          conversation.latest_message?.updated_at?.toString() ?? "",
          conversation.latest_message?.text ?? "",
          ""
        );
      }
    },
    [store, strictLocalize]
  );

  const createOrUpdateConversations = useCallback(
    (
      conversations: IMessengerConversation[],
      simple_brands: Partial<
        Record<string, IGetMessengerConversationsResponse.ISimpleBrand>
      >,
      simple_retailers: Partial<
        Record<string, IGetMessengerConversationsResponse.ISimpleRetailer>
      >
    ) => {
      conversations.forEach((conversation) => {
        if (!conversation.other_party) {
          throw new Error("An unknown error occurred");
        }
        const brand = simple_brands[conversation.brand_token!];
        const retailer = simple_retailers[conversation.retailer_token!];

        createOrUpdateConversation(conversation, brand, retailer);
      });
    },
    [createOrUpdateConversation]
  );

  const fetchConversationByToken = useCallback(
    async (
      conversationToken?: string,
      retailerToken?: string,
      brandToken?: string
    ): Promise<void> => {
      if (!store) {
        return;
      }
      const {
        setSearchResults,
        incrementIsLoadingCount,
        decrementIsLoadingCount,
        brandContext,
        query,
        user,
      } = store;

      try {
        incrementIsLoadingCount();

        let response: IGetConversationResponseV3;

        if (conversationToken) {
          response = await fetchMessengerConversation(conversationToken);
        } else if (user?.type === IUser.Type.BRAND_USER && retailerToken) {
          response = await fetchMessengerConversationByRetailer(retailerToken);
        } else if (user?.type === IUser.Type.USER && brandToken) {
          response = await fetchMessengerConversationByBrand(brandToken);
        } else {
          logError(
            new Error(
              "This function should recieve a conversation_token, or appropriate [brand | retailer]_token relative to the user"
            )
          );
          return;
        }

        const { conversation, simple_brand, simple_retailer } = response;

        if (!conversation || (!simple_retailer && !simple_brand)) {
          logError(
            new Error(
              "This request should return a conversation and information about the retailer or the brand"
            )
          );
          return;
        }

        runInAction(() => {
          if (!query && !brandContext) {
            setSearchResults(undefined);
          }

          if (conversation) {
            createOrUpdateConversation(
              conversation,
              simple_brand,
              simple_retailer
            );
          }
        });
      } catch (e) {
        logError(e);
      } finally {
        decrementIsLoadingCount();
      }
    },
    [store, createOrUpdateConversation]
  );

  const fetchConversations = useCallback(
    async (
      data: Partial<IGetMessengerConversationsRequest> = {},
      isMainStreamFetch?: boolean,
      isSearch?: boolean
    ): Promise<void> => {
      if (!store) {
        return;
      }
      const {
        mainStreamFetchState,
        setTotalUnreadConversations,
        setTotalStarredByBrandConversations,
        setSearchResults,
        brandContext,
        retailerContext,
        query,
        showOnlyUnreadConversations,
        showOnlyStarredByBrandConversations,
      } = store;

      try {
        const {
          conversations,
          total_unread_conversations,
          total_starred_by_brand_conversations,
          simple_brands = {},
          simple_retailers = {},
          next_request,
        } = await findMessengerConversations(
          IGetMessengerConversationsRequest.build({
            ...data,
            context_token:
              data.context_token ??
              brandContext?.token ??
              retailerContext?.token,
            only_unread:
              data.only_unread || showOnlyUnreadConversations || undefined,
            filter: showOnlyStarredByBrandConversations
              ? IGetMessengerConversationsRequest.Filter.STARRED_BY_BRAND
              : undefined,
          })
        );

        if (!conversations) {
          return;
        }

        runInAction(() => {
          if (isSearch) {
            setSearchResults(
              compact(
                conversations.map(
                  (conversation) =>
                    conversation.other_party && conversation.other_party.token
                )
              ).reduce((set, token) => set.add(token), new Set<string>())
            );
          } else if (!query && !brandContext) {
            setSearchResults(undefined);
          }

          if (isMainStreamFetch) {
            const conversationTokens = conversations.map(
              (conversation) => conversation.token
            );
            if (
              isEqual(
                mainStreamFetchState.lastMainStreamConversationRequestTokens,
                conversationTokens
              )
            ) {
              mainStreamFetchState.nextRequest = undefined;
              return;
            }

            mainStreamFetchState.lastMainStreamConversationRequestTokens =
              conversationTokens;
            mainStreamFetchState.nextRequest = next_request;

            setTotalUnreadConversations(total_unread_conversations);
            setTotalStarredByBrandConversations(
              total_starred_by_brand_conversations
            );
          }

          if (brandContext && !simple_brands[brandContext.token!]) {
            simple_brands[brandContext.token!] = brandContext;
          }

          createOrUpdateConversations(
            conversations,
            simple_brands,
            simple_retailers
          );
        });
      } catch (e) {
        logError(e);
      }
    },
    [store, createOrUpdateConversations]
  );

  const nextConversations = useCallback(async () => {
    if (!store) {
      return;
    }
    const { mainStreamFetchState } = store;

    if (mainStreamFetchState.nextRequest) {
      return fetchConversations(mainStreamFetchState.nextRequest, true);
    }
  }, [fetchConversations, store]);

  const searchConversations = useCallback(
    async (
      params: Pick<IGetMessengerConversationsRequest, "context_token" | "query">
    ) => {
      await fetchConversations(params, false, true);
    },
    [fetchConversations]
  );

  /*
   * Async
   *
   * Keeping persistent data that doesn't have an ordering with unlimited scroll is very difficult.
   * - needs to be persistent because
   *   - we don't know if the user is looking at it (scroll)
   * - order of conversation is constantly changing based on which ones have the newest messages
   */

  /**
   * When messenger mounts for the first time this populate conversations and nextRequest for scroll
   * Future calls makes sure all conversations are up to date
   *
   * TODO: should this be debounced? first call does something but subsequent calls do nothing until 200ms later?
   */
  const firstFetchOrUpdateConversations = useCallback(async () => {
    if (!store) {
      return;
    }
    const { mainStreamFetchState } = store;

    if (mainStreamFetchState.isFirstFetch) {
      mainStreamFetchState.isFirstFetch = false;
      return fetchConversations(undefined, true);
    } else {
      // TODO: keep fetching conversations until we find an unchanged conversation
      //   currently this fails if more than 1 page of conversations has changed
      //   requires refactoring fetchConversations and update conversation to return data about what happens
      return fetchConversations();
    }
  }, [fetchConversations, store]);

  const firstFetchOrUpdateMessagesInCurrentConversation =
    useCallback(async () => {
      if (!store) {
        return;
      }
      const {
        currentConversationIdentifier,
        storeUpdatedConversation,
        getConversationByIdentifier,
      } = store;
      if (!currentConversationIdentifier) {
        return;
      }
      const currentConversation = getConversationByIdentifier(
        currentConversationIdentifier
      );
      if (!currentConversation) {
        return;
      }
      storeUpdatedConversation(
        await firstFetchOrUpdateMessages(currentConversation)
      );
    }, [store]);

  const attemptToFindAndSetConversation = useCallback(
    (
      conversationToken?: string,
      brandToken?: string,
      retailerToken?: string,
      otherPartyUserToken?: string
    ): boolean => {
      if (!store) {
        return false;
      }
      const {
        conversationByConversationToken,
        conversationByBrandToken,
        conversationByRetailerToken,
        conversationByUserToken,
        setCurrentConversationIdentifier,
      } = store;

      let conversation: ConversationModel | undefined;

      if (
        conversationToken &&
        conversationByConversationToken.get(conversationToken)
      ) {
        conversation = conversationByConversationToken.get(conversationToken);
      } else {
        // Since we're only getting brand or retailer tokens, we have to use them to do the initial look-up
        if (brandToken && conversationByBrandToken.get(brandToken)) {
          conversation = conversationByBrandToken.get(brandToken);
        } else if (
          retailerToken &&
          conversationByRetailerToken.get(retailerToken)
        ) {
          conversation = conversationByRetailerToken.get(retailerToken);
        } else if (
          otherPartyUserToken &&
          conversationByUserToken.get(otherPartyUserToken)
        ) {
          conversation = conversationByUserToken.get(otherPartyUserToken);
        }

        // If we found a real conversation, let's pull it from the conversation_token indexed list for consistency
        if (
          conversation &&
          getConversationModelToken(conversation) &&
          conversationByConversationToken.get(
            getConversationModelToken(conversation)
          )
        ) {
          conversation = conversationByConversationToken.get(
            getConversationModelToken(conversation)
          );
        }
      }

      if (conversation) {
        setCurrentConversationIdentifier(
          getConversationModelIdentifier(conversation)
        );
        return true;
      }
      return false;
    },
    [store]
  );

  const setCurrentConversation = useCallback(
    async (
      param: {
        otherPartyUserToken?: string;
        brandToken?: string;
        retailerToken?: string;
        conversationToken?: string;
      } = {}
    ): Promise<void> => {
      if (!store) {
        return;
      }
      const { setCurrentConversationIdentifier } = store;

      const {
        otherPartyUserToken,
        brandToken,
        retailerToken,
        conversationToken,
      } = param;

      if (
        !conversationToken &&
        !otherPartyUserToken &&
        !brandToken &&
        !retailerToken
      ) {
        setCurrentConversationIdentifier(undefined);
        return;
      }

      if (
        attemptToFindAndSetConversation(
          conversationToken,
          brandToken,
          retailerToken,
          otherPartyUserToken
        )
      ) {
        return;
      }
      await fetchConversationByToken(
        conversationToken,
        retailerToken,
        brandToken
      );
      if (
        !attemptToFindAndSetConversation(
          conversationToken,
          brandToken,
          retailerToken,
          otherPartyUserToken
        )
      ) {
        throw new Error(
          "A conversation should have been returned by the server."
        );
      }
    },
    [store, attemptToFindAndSetConversation, fetchConversationByToken]
  );

  const hasConversationsNextRequest = store
    ? !!store.mainStreamFetchState?.nextRequest
    : false;

  return useMemo(
    () => ({
      createOrUpdateConversation,
      nextConversations,
      searchConversations,
      fetchConversations,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
      setCurrentConversation,
      hasConversationsNextRequest,
    }),
    [
      createOrUpdateConversation,
      nextConversations,
      searchConversations,
      fetchConversations,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
      setCurrentConversation,
      hasConversationsNextRequest,
    ]
  );
};
